/**
 * Auth composable — singleton reactive state for the LINE Login flow.
 *
 * The auth cookie (`lw_auth`) is httpOnly — the SPA never reads or touches it
 * directly. This module bootstraps auth state by calling GET /api/auth/line/status
 * once on startup; subsequent views re-read the cached state.
 *
 * State machine:
 *   loading        – /status call in flight (branded splash)
 *   unauthenticated – no cookie / token expired (show login)
 *   pending        – authenticated but approved=false (show pending screen)
 *   approved       – valid cookie, approved=true (show dashboard)
 *   revoked        – was approved, now approved=false mid-session (show revoked + logout)
 *
 * bead: lucky-war-hw7.1
 */
import { ref, shallowRef } from 'vue';

// ── Auth state enum ───────────────────────────────────────────────────────────

export const AuthState = {
  Loading: 'loading',
  Unauthenticated: 'unauthenticated',
  Pending: 'pending',
  Approved: 'approved',
  Revoked: 'revoked',
} as const;

export type AuthState = (typeof AuthState)[keyof typeof AuthState];

// ── Status response shape ─────────────────────────────────────────────────────

/** Shape returned by GET /api/auth/line/status (always HTTP 200). */
export interface AuthStatus {
  authenticated: boolean;
  approved: boolean;
  superuser: boolean;
  role: string;
  display_name: string;
  id: string;
}

// ── Module-level singleton state ──────────────────────────────────────────────
// Created at module scope so every component imports the same reactive instance.

const state = shallowRef<AuthState>(AuthState.Loading);
const displayName = ref<string | null>(null);
const userId = ref<string | null>(null);
/** Current approved user's role (viewer or admin). */
const role = ref<string | null>(null);
/** Whether the current authenticated session is a superuser (admin). Drives superuser-only UI. */
const isSuperuser = ref(false);
/** Set to true when a 401 is received mid-session (expired token). LoginView reads this to show a banner. */
const sessionExpired = ref(false);
const bootstrapPromise: { current: Promise<void> | null } = { current: null };

// ── Bootstrap ─────────────────────────────────────────────────────────────────

/**
 * Call GET /api/auth/line/status once on startup.
 * Idempotent — subsequent calls return immediately if already resolved.
 * Call this from App.vue (or main.ts before mount) so the router guard can
 * gate dashboard routes.
 */
export async function bootstrapAuth(): Promise<void> {
  // Already resolved — return the cached state.
  if (state.value !== AuthState.Loading) return;

  // Already in flight — wait for the existing promise.
  if (bootstrapPromise.current) return bootstrapPromise.current;

  bootstrapPromise.current = (async () => {
    try {
      const res = await fetch('/api/auth/line/status');
      const body = (await res.json()) as AuthStatus;

      if (!body.authenticated || !body.approved) {
        // No valid session cookie. Could be:
        //   - fresh visitor (unauthenticated)
        //   - pending activation (admin hasn't approved yet)
        //   - expired session
        // /status cannot distinguish these without a cookie; they all surface
        // as unauthenticated. The pending view is reachable via the callback
        // redirect — see PendingView.vue for the re-login flow.
        state.value = AuthState.Unauthenticated;
        displayName.value = null;
        userId.value = null;
        role.value = null;
        isSuperuser.value = false;
        return;
      }

      // Valid cookie with approved=true.
      state.value = AuthState.Approved;
      displayName.value = body.display_name || null;
      userId.value = body.id || null;
      role.value = body.role || null;
      isSuperuser.value = !!body.superuser;
    } catch {
      // Network error or unexpected response — treat as unauthenticated so
      // the user sees the login screen rather than an infinite spinner.
      state.value = AuthState.Unauthenticated;
      displayName.value = null;
      userId.value = null;
      role.value = null;
      isSuperuser.value = false;
    }
  })();

  return bootstrapPromise.current;
}

/**
 * Force-recheck auth status (e.g. after the LINE OAuth callback redirected
 * back to the SPA). Resets to loading, re-fetches /status, and returns the
 * new state.
 */
export async function recheckAuth(): Promise<AuthState> {
  state.value = AuthState.Loading;
  bootstrapPromise.current = null;

  try {
    const res = await fetch('/api/auth/line/status');
    const body = (await res.json()) as AuthStatus;

    if (!body.authenticated || !body.approved) {
      state.value = AuthState.Unauthenticated;
      displayName.value = null;
      userId.value = null;
      role.value = null;
      isSuperuser.value = false;
    } else {
      state.value = AuthState.Approved;
      displayName.value = body.display_name || null;
      userId.value = body.id || null;
      role.value = body.role || null;
      isSuperuser.value = !!body.superuser;
    }
  } catch {
    state.value = AuthState.Unauthenticated;
    displayName.value = null;
    userId.value = null;
    role.value = null;
    isSuperuser.value = false;
  }

  return state.value;
}

// ── Actions ───────────────────────────────────────────────────────────────────

/**
 * Logout: POST /api/auth/line/logout (clears the httpOnly cookie server-side),
 * then reset local state to unauthenticated.
 */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/line/logout', { method: 'POST' });
  } catch {
    // Even if the server call fails, clear local state so the UI reflects
    // the logout intent.
  }
  state.value = AuthState.Unauthenticated;
  displayName.value = null;
  userId.value = null;
  role.value = null;
}

/** Called by the API client interceptor when a 403 access_revoked is received. */
export function setRevoked(): void {
  state.value = AuthState.Revoked;
}

/** Called by the API client interceptor on 401 (expired session). */
export function setUnauthenticated(): void {
  // If transitioning from approved→unauthenticated, it's a session expiry
  // (vs a fresh visit). The LoginView reads this flag to show a banner.
  if (state.value === AuthState.Approved) {
    sessionExpired.value = true;
  }
  state.value = AuthState.Unauthenticated;
  displayName.value = null;
  userId.value = null;
  role.value = null;
}

/** Called by the API client interceptor on 403 pending_activation. */
export function setPending(): void {
  state.value = AuthState.Pending;
}

// ── Read accessors (useAuth composable) ──────────────────────────────────────

/**
 * The singleton auth composable. Returns module-level reactive refs so every
 * consumer reads the same state.
 */
export function useAuth() {
  return {
    state,
    displayName,
    userId,
    role,
    isSuperuser,
    sessionExpired,
    // Actions (re-exported for convenience)
    bootstrapAuth,
    recheckAuth,
    logout,
    setRevoked,
    setUnauthenticated,
    setPending,
  };
}
